// Adapter Outlook: unico punto che conosce le colonne di `outlook_messages`,
// la cifratura dei campi e le edge function microsoft-outlook-*.
// Speculare a gmailAdapter: mappa le righe DB (message_id / conversation_id) su
// MailMessage (message_id / thread_id) e SINTETIZZA `label_ids` per la UI dal
// modello Microsoft Graph (cartella + is_flagged + categories).

import { supabase } from '../../../supabase';
import { decryptMessage } from '../../../utils/message-crypto';
import type { MailMessage, MailLabel, DraftSummary } from '../types';
import type { MailAdapter, MailModifyAction, MailSendParams, MailDraftParams } from '../mailAdapter';

const SELECT = '*, client:clients(id, business_name, contact_name, logo_url, email)';

// Token canonici di cartella (Graph wellKnownName → token UI condiviso col menu Gmail).
const FOLDER_TOKENS: Record<string, string> = {
  inbox: 'INBOX',
  sentitems: 'SENT',
  drafts: 'DRAFT',
};

// Riga DB → MailMessage. Rinomina message_id/conversation_id e sintetizza label_ids:
//  - token della cartella (inbox→INBOX, sentitems→SENT, drafts→DRAFT)
//  - 'STARRED' se is_flagged
//  - i nomi delle categories come label utente
// thread_id = conversation_id con fallback su message_id (conversation_id è nullable).
function mapRow(row: any): MailMessage {
  const { message_id, conversation_id, folder_id, folder_well_known, categories, is_flagged, ...rest } = row;

  const label_ids: string[] = [];
  const folderToken = folder_well_known ? FOLDER_TOKENS[folder_well_known] : undefined;
  if (folderToken) label_ids.push(folderToken);
  if (is_flagged) label_ids.push('STARRED');
  if (Array.isArray(categories)) label_ids.push(...categories);

  return {
    ...rest,
    message_id,
    thread_id: conversation_id || message_id,
    label_ids,
    is_starred: !!is_flagged,
  } as MailMessage;
}

// Decifra i campi cifrati di un messaggio (stessi campi di gmailAdapter + body).
async function decryptMailMessage(msg: MailMessage, organizationId: string): Promise<MailMessage> {
  const [subject, from_name, body_html, body_text] = await Promise.all([
    msg.subject ? decryptMessage(msg.subject, organizationId) : Promise.resolve(msg.subject),
    msg.from_name ? decryptMessage(msg.from_name, organizationId) : Promise.resolve(msg.from_name),
    msg.body_html ? decryptMessage(msg.body_html, organizationId) : Promise.resolve(msg.body_html),
    msg.body_text ? decryptMessage(msg.body_text, organizationId) : Promise.resolve(msg.body_text),
  ]);
  return { ...msg, subject, from_name, body_html, body_text };
}

async function normalize(rows: any[] | null, organizationId: string): Promise<MailMessage[]> {
  const mapped = (rows || []).map(mapRow);
  return Promise.all(mapped.map(m => decryptMailMessage(m, organizationId)));
}

// MailComposeDrawer (condiviso) costruisce inReplyTo come `<{message_id}@mail.gmail.com>`
// (formato RFC Message-ID nato per Gmail). Per Outlook serve l'ImmutableId grezzo del
// messaggio a cui rispondere: qui rimuoviamo il wrapping se presente.
// Fragile: accoppiato al literal `@mail.gmail.com` scelto dal compose condiviso, che
// non è provider-aware. Se in futuro cambia il formato costruito in
// MailComposeDrawer.handleSend, questa regex va aggiornata di conseguenza (fix pulito
// = compose provider-aware o Message-ID neutro, fuori scope qui).
function extractOutlookMessageId(inReplyTo?: string): string | undefined {
  if (!inReplyTo) return undefined;
  const m = inReplyTo.match(/^<(.+)@mail\.gmail\.com>$/);
  return m ? m[1] : inReplyTo;
}

// Invoke di una edge microsoft-outlook-* con gestione dell'envelope errori Outlook:
//  - HTTP non-2xx → supabase-js valorizza `error` (FunctionsHttpError) e il body è in
//    error.context; proviamo a estrarne `message` per un errore parlante.
//  - HTTP 200 con `{ error, message }` (difensivo) → rilanciamo come Error.
async function invokeOutlook(fn: string, body: Record<string, any>): Promise<any> {
  const { data, error } = await supabase.functions.invoke(fn, { body });
  if (error) {
    let message: string | null = null;
    try {
      const ctxBody = await (error as any)?.context?.json?.();
      message = ctxBody?.message ?? null;
    } catch {
      message = null;
    }
    throw message ? new Error(message) : error;
  }
  if (data && typeof data === 'object' && 'error' in data && data.error) {
    throw new Error(data.message || String(data.error));
  }
  return data;
}

export const outlookAdapter: MailAdapter = {
  provider: 'microsoft',
  storagePrefix: 'outlook_view_mode',
  // send() con draftId invia la bozza STESSA (PATCH + POST /send): la bozza è consumata
  // dall'invio e non va cancellata a valle (ImmutableId: stesso id sul messaggio inviato).
  consumesDraftOnSend: true,

  async fetchMessages(orgId, userId, label, limit) {
    let query = supabase
      .from('outlook_messages')
      .select(SELECT)
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .order('internal_date', { ascending: false });

    if (label === 'INBOX') {
      query = query.eq('folder_well_known', 'inbox');
    } else if (label === 'SENT') {
      query = query.eq('folder_well_known', 'sentitems');
    } else if (label === 'STARRED') {
      query = query.eq('is_flagged', true);
    } else if (label === 'ALL') {
      // 'ALL' = tutte le cartelle TRANNE il cestino (parità Gmail, che elimina del tutto
      // i cestinati dalla cache: nessuna vista Cestino su nessuno dei due provider). NB:
      // le righe delle cartelle custom hanno folder_well_known NULL e DEVONO restare
      // visibili → filtro esplicito che tiene i NULL (un `.neq` da solo li escluderebbe).
      query = query.or('folder_well_known.is.null,folder_well_known.neq.deleteditems');
    } else if (label === 'DRAFT') {
      // 'DRAFT' = filtro client-side (nella factory): le bozze Outlook non sono in
      // outlook_messages ma nella mappa drafts.
    } else if (label.startsWith('FOLDER:')) {
      // Cartella custom di primo livello: naviga per folder_id (id Graph dopo il namespace).
      query = query.eq('folder_id', label.slice('FOLDER:'.length));
    } else {
      // Label utente = nome categoria.
      query = query.contains('categories', [label]);
    }

    const { data, error } = await query.limit(limit);
    if (error) throw error;
    return normalize(data, orgId);
  },

  async searchMessages(orgId, userId, query) {
    // Ricerca solo sui campi in chiaro (from_email, snippet).
    const sanitized = query.replace(/[%_]/g, '\\$&').replace(/[,.()"']/g, '');
    const { data, error } = await supabase
      .from('outlook_messages')
      .select(SELECT)
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .or(`from_email.ilike.%${sanitized}%,snippet.ilike.%${sanitized}%`)
      .order('internal_date', { ascending: false })
      .limit(200);
    if (error) throw error;
    return normalize(data, orgId);
  },

  async fetchThreadMessages(orgId, userId, threadId) {
    const { data, error } = await supabase
      .from('outlook_messages')
      .select(SELECT)
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .eq('conversation_id', threadId)
      .order('internal_date', { ascending: true });
    if (error) throw error;
    return normalize(data, orgId);
  },

  async updateReadFlag(orgId, userId, messageRowIds, isRead) {
    await supabase
      .from('outlook_messages')
      .update({ is_read: isRead, updated_at: new Date().toISOString() })
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .in('id', messageRowIds);
  },

  async updateStarFlag(orgId, userId, threadId, starred) {
    // Il flag Outlook è per-messaggio: si aggiorna la sola riga più recente della
    // conversation (la stessa su cui l'edge modify applica star/unstar).
    const { data: latest } = await supabase
      .from('outlook_messages')
      .select('id')
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .eq('conversation_id', threadId)
      .order('internal_date', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (!latest) return;
    await supabase
      .from('outlook_messages')
      .update({ is_flagged: starred, updated_at: new Date().toISOString() })
      .eq('id', latest.id);
  },

  async updateThreadClient(orgId, userId, threadId, clientId) {
    await supabase
      .from('outlook_messages')
      .update({ client_id: clientId, updated_at: new Date().toISOString() })
      .eq('organization_id', orgId)
      .eq('user_id', userId)
      .eq('conversation_id', threadId);
  },

  async sync(orgId, _userId, fullSync) {
    // L'edge ricava user/org dal JWT+profilo; passiamo organization_id (validato lato
    // edge contro il profilo) e full_sync.
    return invokeOutlook('microsoft-outlook-sync', {
      organization_id: orgId,
      full_sync: fullSync,
    });
  },

  async fetchFullMessage(_orgId, _userId, messageId) {
    return invokeOutlook('microsoft-outlook-message', { message_id: messageId });
  },

  async modify(_orgId, _userId, messageId, action: MailModifyAction, labelId) {
    const body: Record<string, any> = { message_id: messageId, action };
    if (action === 'move') {
      // Per 'move' il parametro `labelId` trasporta l'id della cartella di destinazione
      // (l'edge lo attende come `destination_folder_id`). Difensivo: rimuove l'eventuale
      // namespace 'FOLDER:' se il chiamante passa l'id di label anziché quello Graph grezzo.
      const dest = (labelId || '').startsWith('FOLDER:') ? labelId!.slice('FOLDER:'.length) : labelId;
      body.destination_folder_id = dest;
    } else if (labelId !== undefined) {
      body.label_id = labelId;
    }
    await invokeOutlook('microsoft-outlook-modify', body);
  },

  async send(params: MailSendParams) {
    await invokeOutlook('microsoft-outlook-send', {
      // Bozza corrente: se presente l'edge la invia DIRETTAMENTE (PATCH + send) invece
      // di creare un messaggio nuovo → gli allegati già sulla bozza sono preservati.
      // `attachments: undefined` viene omesso dal JSON → semantica 3 stati lato edge
      // (bozza inviata con gli allegati che già ha).
      draft_id: params.draftId || undefined,
      to: params.to,
      cc: params.cc,
      bcc: params.bcc,
      subject: params.subject,
      body_html: params.bodyHtml,
      in_reply_to_message_id: extractOutlookMessageId(params.inReplyTo),
      // replyMode arriva dal compose (MailSendParams.replyMode): l'edge usa
      // createReplyAll/createForward invece di degradare tutto a createReply. Se
      // assente (compose nuovo) l'edge ricade su 'reply' se c'è
      // in_reply_to_message_id, altrimenti 'compose'.
      reply_mode: params.replyMode,
      attachments: params.attachments,
      track: params.track,
    });
  },

  async saveDraft(params: MailDraftParams) {
    const data = await invokeOutlook('microsoft-outlook-draft', {
      action: 'save',
      draft_id: params.draftId || undefined,
      to: params.to,
      cc: params.cc,
      bcc: params.bcc,
      subject: params.subject,
      body_html: params.bodyHtml,
      // Threading nativo: sulla PRIMA creazione l'edge usa createReply/createReplyAll/
      // createForward a partire dall'ImmutableId del messaggio originale (de-wrappato dal
      // Message-ID che il compose costruisce in stile Gmail), ereditando il conversationId
      // reale. reply_mode distingue reply/replyAll/forward (default 'reply' se assente ma
      // c'è in_reply_to_message_id, altrimenti 'compose'). Graph non consente di impostare
      // conversationId a mano: threadId viaggia solo per completezza del contratto.
      conversation_id: params.threadId,
      in_reply_to_message_id: extractOutlookMessageId(params.inReplyTo),
      reply_mode: params.replyMode,
      // Allegati sincronizzati in modo idempotente lato edge (firma nome+size): l'auto-save
      // ogni 2s re-invia l'intero set senza duplicare né perdere gli allegati.
      attachments: params.attachments,
    });
    return data?.draft_id || null;
  },

  async deleteDraft(_orgId, _userId, draftId) {
    await invokeOutlook('microsoft-outlook-draft', { action: 'delete', draft_id: draftId });
  },

  async listDrafts(_orgId, _userId): Promise<DraftSummary[]> {
    const data = await invokeOutlook('microsoft-outlook-drafts-list', {});
    return data?.drafts || [];
  },

  async loadLabels(_orgId, _userId): Promise<MailLabel[]> {
    const data = await invokeOutlook('microsoft-outlook-folders', {});
    return data?.labels || [];
  },

  async downloadAttachment(_orgId, _userId, messageId, attachmentId) {
    const data = await invokeOutlook('microsoft-outlook-attachment', {
      message_id: messageId,
      attachment_id: attachmentId,
    });
    return data.data;
  },
};
